import { type ReactNode } from "react";
import * as Sentry from "@sentry/react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { MotionConfig } from "motion/react";
import { RouterProvider } from "react-router-dom";
import { router } from "./router";
import { ChunkErrorBoundary } from "@/components/ChunkErrorBoundary";
import { Toaster } from "@/components/Toaster";
import styles from "./AppError.module.css";

/**
 * Single shared QueryClient.
 *
 * Defaults chosen for shotflux's workload:
 * - Lists (projects, scenes, characters) tolerate ~30s staleness; refetching
 *   on every focus would be noisy during a long session.
 * - Refetch on window focus is OFF for the same reason; data only refreshes
 *   on explicit mutate or manual navigation.
 * - Retries: 1. Network blips are common; 2+ retries hide real bugs.
 */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: 5 * 60_000,
      refetchOnWindowFocus: false,
      retry: 1,
    },
    mutations: {
      retry: 0,
    },
  },
});

/**
 * Renders the same UI the hand-rolled RootErrorBoundary used to.
 * Sentry.ErrorBoundary passes `error` and `resetError` props; we ignore
 * resetError and use a full page reload because by the time this root
 * boundary fires the app shell is unrecoverable.
 *
 * Replacing the class-based RootErrorBoundary with Sentry.ErrorBoundary
 * collapses two layers (telemetry + fallback) into one — Sentry.ErrorBoundary
 * auto-reports to Sentry AND renders the fallback. The fallback UI
 * (Reload button + stack trace pre) is preserved verbatim.
 */
function FallbackUI({ error }: { error: unknown }): ReactNode {
  const err = error instanceof Error ? error : new Error(String(error));
  return (
    <div className={styles.page} role="alert">
      <header className={styles.header}>
        <h1>Something went wrong</h1>
        <p className={styles.body}>
          The interface hit an unexpected error. Reload the page to recover;
          if it keeps happening, this is a bug worth filing.
        </p>
      </header>
      <pre className={styles.trace}>
        {err.message}
        {err.stack ? `\n\n${err.stack}` : ""}
      </pre>
      <div className={styles.actions}>
        <button
          type="button"
          className="primary"
          onClick={() => window.location.reload()}
        >
          Reload
        </button>
      </div>
    </div>
  );
}

export function App() {
  return (
    <Sentry.ErrorBoundary
      fallback={({ error }) => <FallbackUI error={error} />}
      // showDialog: false — we render our own UI. Sentry's auto-dialog is
      // for the "user feedback" flow which we don't want yet.
      showDialog={false}
    >
      <QueryClientProvider client={queryClient}>
        {/* `reducedMotion="user"` lets motion/react auto-disable transitions
            when the OS-level prefers-reduced-motion media query matches.
            All `motion.*` components inside inherit this config. */}
        <MotionConfig reducedMotion="user">
          {/* ChunkErrorBoundary sits just inside RouterProvider so a
              failed lazy-route fetch (common right after a deploy when an
              open tab references a now-removed chunk hash) auto-reloads
              the user instead of showing a stack trace. */}
          <ChunkErrorBoundary>
            <RouterProvider router={router} />
          </ChunkErrorBoundary>
          <Toaster />
        </MotionConfig>
        {import.meta.env.DEV ? <ReactQueryDevtools initialIsOpen={false} /> : null}
      </QueryClientProvider>
    </Sentry.ErrorBoundary>
  );
}
